import { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { OpenInNew } from '@material-ui/icons'

import { TOAST_TYPE, useTableReducer } from '@base'
import { Table, TableActionButton, TableActionsWrapper, Tooltip } from '@baseV2'
import { DRAWER_TYPES, strings, tenderBidderSearchFilter } from '@constants'
import { getTenderBookCompanies } from '@data'
import { showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { useForceUpdate } from '@hooks'
import { getTenderbookCompanyLink } from '@utils'
import { FullHeightPageLevelTableWrapper } from '@pages/Admin/Tenderbook/styles'
import { CopyText } from '@pages/Admin/Tenderbook/UserList/styles'
import { PrimaryText, SecondaryText, StackCell } from '@pages/Admin/Tenderbook/UserList/UserDetails/styles'
import { formatAmount } from '@pages/Admin/Tenderbook/UserList/UserDetails/utils'

const ROWS_PER_PAGE = 10

const getCompanyProfileLink = (row) => {
	const link = getTenderbookCompanyLink(row?.bidder_detail_id)
	return link ? `${link}/overview` : null
}

const TenderbookCompanies = () => {
	const [state, dispatch, actions] = useTableReducer(tenderBidderSearchFilter)
	const globalDispatch = useDispatch()
	const [forceUpdate, update] = useForceUpdate()

	useEffect(() => {
		if (state.loading) return

		dispatch(actions.fetchTableDataProgress())
		getTenderBookCompanies({ ...state.query.filters, page: state.query.page, size: ROWS_PER_PAGE })
			.then((response) => {
				dispatch(actions.fetchTableDataSuccess(response.data, response.count))
			})
			.catch((error) => {
				dispatch(actions.fetchTableDataFailure(error))
			})
	}, [state.query, forceUpdate])

	const columns = useMemo(
		() => [
			{
				label: 'Organisation Name',
				key: (row) => row,
				sortable: false,
				mobile: true,
				renderer: (row) => (
					<StackCell>
						<PrimaryText>
							{row?.organisation_name ? (
								<CopyText onClick={(e) => copyOrganisationName(e, row.org_name)}>{row.organisation_name}</CopyText>
							) : (
								'-'
							)}
						</PrimaryText>
						<SecondaryText>{row?.pan || '-'}</SecondaryText>
					</StackCell>
				),
			},
			{ label: 'GSTIN', key: 'gstn', sortable: false, mobile: true },
			{ label: 'State', key: 'state', sortable: false },
			{
				label: 'Authorities',
				key: 'authorities',
				sortable: false,
				// First authority inline; the full list is on hover when there are more.
				renderer: (authorities) => {
					if (!Array.isArray(authorities) || !authorities.length) return '-'
					if (authorities.length === 1) return authorities[0]
					return (
						<Tooltip content={authorities.join(', ')}>
							<span>{`${authorities[0]} +${authorities.length - 1}`}</span>
						</Tooltip>
					)
				},
			},
			// Stringified so a genuine 0 shows as 0 rather than the table's empty fallback.
			{ label: 'Participated Tenders', key: (row) => String(row?.participated_tenders ?? 0), sortable: false },
			{ label: 'Won Tenders', key: (row) => String(row?.won_tenders ?? 0), sortable: false, mobile: true },
			{ label: 'Won Value', key: (row) => (row?.won_value ? formatAmount(row.won_value) : '-'), sortable: false },
			{
				label: 'Actions',
				align: 'center',
				sortable: false,
				mobile: true,
				key: (row) => row,
				renderer: (row) => {
					const link = getCompanyProfileLink(row)
					if (!link) return '-'
					return (
						<TableActionsWrapper>
							<TableActionButton title='View on Tenderbook' onClick={(e) => openCompanyProfile(e, link)}>
								<OpenInNew />
							</TableActionButton>
						</TableActionsWrapper>
					)
				},
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	// Copies `org_name` (the key the merge API matches on) instead of opening the manage drawer for the row.
	const copyOrganisationName = (e, name) => {
		e.stopPropagation()
		if (!name) return
		navigator.clipboard.writeText(name)
		globalDispatch(showToast(true, strings(['organisation', 'name', 'copied']), { type: TOAST_TYPE.INFO }))
	}

	const onRowClickHandle = (row) => {
		if (!row?.bidder_detail_id) return
		globalDispatch(toggleDrawer(true, DRAWER_TYPES.TENDERBOOK_MANAGE_BIDDER, { label: 'Manage Organisation', update, data: row }))
	}

	const openCompanyProfile = (e, link) => {
		e.stopPropagation()
		window.open(link, '_blank', 'noopener,noreferrer')
	}

	const changePageHandler = (next = true) => {
		dispatch(actions.changePage(next))
	}

	return (
		<FullHeightPageLevelTableWrapper>
			<Table
				data={state.data}
				columns={columns}
				loading={state.loading}
				rowsToDisplay={ROWS_PER_PAGE}
				totalItemCount={state.total_count}
				page={state.query.page}
				onPageChange={changePageHandler}
				onRowClick={onRowClickHandle}
				emptyState={{ text: 'No companies found' }}
				scrollable
			/>
		</FullHeightPageLevelTableWrapper>
	)
}

export default TenderbookCompanies
